import React, { useState, useRef, memo } from 'react';
import { Badge, IconButton, Menu, MenuItem, Typography, Box } from '@mui/material';
import MessageIcon from '@mui/icons-material/Message';
import { useNavigate } from 'react-router-dom';
import { useAuthReady } from '@/hooks/useAuthReady';
import { useMessagesBootstrap } from '@/hooks/useMessagesBootstrap';
import { isClientUser } from '@/app/router/helpers';
import { parseAsUtc } from '../../../utils/dateUtils';

/** Minimum ms between on-click refreshes to avoid redundant API calls. */
const CLICK_DEBOUNCE_MS = 5_000;

function MessagesCenter() {
  const { user } = useAuthReady();
  const [anchorEl, setAnchorEl] = useState(null);
  const lastClickRefresh = useRef(0);
  const navigate = useNavigate();

  const open = Boolean(anchorEl);

  // Shared messages bootstrap — Layout, MessagesCenter, and
  // ConversationContext all read from this single cached query so a
  // cold page load issues one request instead of three.  Backend
  // returns `null` sections for client users so this hook is safe to
  // call from any authenticated component.
  const { data: bootstrap, refetch: refetchBootstrap } = useMessagesBootstrap();
  const careUnreadCount = bootstrap ? (bootstrap?.unread_count?.care_unread || 0) : null;
  const messageThreads = bootstrap?.inbox_preview?.messages || [];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Refresh on open, but skip if we just refreshed (rapid open/close)
    const now = Date.now();
    if (now - lastClickRefresh.current >= CLICK_DEBOUNCE_MS) {
      lastClickRefresh.current = now;
      refetchBootstrap();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewAllMessages = () => {
    navigate('/messages');
    handleClose();
  };

  const handleMessageClick = (patientId) => {
    navigate(`/messages?patient=${patientId}`);
    handleClose();
  };

  // Don't render for client users - they use client portal
  if (isClientUser(user)) {
    return null;
  }

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="messages"
        title="Secure Messages"
      >
        <Badge badgeContent={careUnreadCount} color="error">
          <MessageIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 320, maxHeight: 400 }
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Messages{careUnreadCount ? ` (${careUnreadCount} unread)` : ''}
          </Typography>
        </Box>

        {careUnreadCount === 0 ? (
          <MenuItem onClick={handleViewAllMessages}>
            <Typography color="text.secondary">
              No new messages
            </Typography>
          </MenuItem>
        ) : (
          <>
            {messageThreads.map((message) => (
              <MenuItem
                key={message.id}
                onClick={() => handleMessageClick(message.patient_id)}
              >
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {message.patient_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {message.preview}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {parseAsUtc(message.timestamp).format('MMM D, YYYY h:mm A')}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
            
            {careUnreadCount > messageThreads.length && (
              <MenuItem disabled>
                <Typography variant="caption" color="text.secondary">
                  +{careUnreadCount - messageThreads.length} more messages
                </Typography>
              </MenuItem>
            )}
          </>
        )}

        <MenuItem onClick={handleViewAllMessages} sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="primary" fontWeight="bold">
            View All Messages
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
}

export default memo(MessagesCenter);
